/** Settings: where and how AI jobs run (import, write, detect): the model, the machine, the budget and the time limit per job. */
import { Sparkles } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import { Button, EmptyState, ErrorState, Input, Select, Skeleton, toast } from '@orgus/ui';
import { toUiError } from '../../../lib/api.ts';
import type { UiError } from '../../../lib/api.ts';
import { useMachines } from '../../../lib/store.ts';
import { AI_MODELS } from '../../requirements/AiRunOptions.tsx';
import { saveAiSettings, useAiJobs } from '../../requirements/data.ts';
import type { SettingsSection } from '../registry.ts';

function AiWriting() {
  const { state, phase } = useAiJobs();
  const machines = useMachines();
  const [model, setModel] = useState('sonnet');
  const [machineId, setMachineId] = useState('');
  const [budget, setBudget] = useState('2');
  const [minutes, setMinutes] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!state) return;
    setModel(state.settings.model);
    setMachineId(state.settings.machineId ?? '');
    setBudget(String(state.settings.maxBudgetUsd));
    setMinutes(String(Math.round(state.settings.timeoutMs / 60_000)));
    // Only when the hub's settings change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.settings]);

  if (phase === 'loading') return <Skeleton className="h-48" />;
  if (!state) {
    return <EmptyState icon={<Sparkles />} title="This hub does not run AI jobs" description="Restart the hub on the current Orgus version to import documents and let AI write requirements." />;
  }

  const budgetN = Number(budget);
  const minutesN = Number(minutes);
  const errors = {
    budget: !(budgetN > 0 && budgetN <= 100) ? 'Enter an amount above $0 and up to $100.' : undefined,
    minutes: !(minutesN >= 1 && minutesN <= 60) ? 'Enter 1 to 60 minutes.' : undefined,
  };
  const save = async () => {
    setTouched(true);
    if (errors.budget || errors.minutes) return;
    setBusy(true);
    setError(null);
    try {
      await saveAiSettings({ model, machineId: machineId || null, maxBudgetUsd: budgetN, timeoutMs: Math.round(minutesN * 60_000) });
      toast.success('AI settings saved', `${AI_MODELS.find((m) => m.value === model)?.label ?? model}, up to $${budgetN.toFixed(2)} a job.`);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex max-w-[640px] flex-col gap-4">
      <p className="text-ui text-fg-muted">
        Importing a document, writing, improving, splitting and estimating items, and finding duplicates each run Claude Code headless on one of your machines, with that machine's own login.
        No API key is stored. Every job shows on Missions, Review, with its cost, and nothing is created until you review it.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Select
          label="Model"
          value={model}
          onValueChange={setModel}
          options={AI_MODELS.some((m) => m.value === model) ? AI_MODELS : [...AI_MODELS, { value: model, label: model }]}
          hint="Sonnet by default. Haiku is faster and cheaper for small documents."
        />
        <Select
          label="Machine"
          value={machineId}
          onValueChange={setMachineId}
          options={[{ value: '', label: 'The hub picks' }, ...machines.map((m) => ({ value: m.id, label: m.online ? m.name : `${m.name} (offline)` }))]}
          hint="The hub picks its own machine, else the least busy one."
        />
        <Input label="Budget per job (USD)" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} error={touched ? errors.budget : undefined} hint="Claude Code stops a job that would cost more." />
        <Input label="Time limit (minutes)" inputMode="numeric" value={minutes} onChange={(e) => setMinutes(e.target.value)} error={touched ? errors.minutes : undefined} />
      </div>
      {error && <ErrorState variant="inline" error={error} />}
      <div>
        <Button variant="primary" loading={busy} onClick={save}>
          Save AI settings
        </Button>
      </div>
    </div>
  );
}

const section: SettingsSection = {
  id: 'ai-writing',
  title: 'AI writing',
  description: 'Model, machine and limits for AI writing and imports.',
  order: 60,
  group: 'agents',
  keywords: ['ai', 'import', 'requirements', 'drafts', 'model'],
  icon: Sparkles,
  Component: AiWriting,
  needs: { module: 'aijobs', feature: 'AI writing', instead: 'Start a session from Sessions to draft with an agent.' },
};
export default section;
